import React, { useEffect, useState, useMemo } from 'react';
import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { InputStyled } from '@components/dashboard/SharedStyles';
import Colors from '../Colors';
import UnitSwapToggle from './UnitSwapToggle';

interface Props {
  id: string;
  initialValue?: string | number | null;
}

const HeightInput: React.FC<Props> = ({ id, initialValue }) => {
  const { setFieldValue } = useFormikContext();
  const units = ['cm', 'ft'];
  const [currentUnit, setCurrentUnit] = useState(units[0]);
  const defaultValues = { cm: undefined, ft: undefined, in: undefined };
  const [unitValues, setUnitValues] = useState(defaultValues);

  useEffect(() => {
    if (!unitValues) return;
    if (currentUnit === 'cm' && unitValues.cm) {
      setFieldValue(id, unitValues.cm);
    } else if (currentUnit === 'ft' && unitValues.ft) {
      const cmTotal = unitValues.ft * 30.48 + (unitValues?.in ?? 0) * 2.54;
      const roundedValue = Math.round((cmTotal + Number.EPSILON) * 100) / 100;
      if (!Number.isNaN(roundedValue)) setFieldValue(id, roundedValue);
    }
  }, [unitValues, currentUnit, id, setFieldValue]);

  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    const numberValue = Number(value);
    const newValues = { ...unitValues, [name as string]: numberValue };
    setUnitValues(newValues);
  };

  const handleUnitSwap = (unit: string) => {
    setFieldValue(id, 0);
    setUnitValues(defaultValues);
    setCurrentUnit(unit);
  };

  // Memoize the rendered input to prevent unnecessary re-renders
  const renderedInput = useMemo(() => {
    const isCentimeters = currentUnit === 'cm';
    const isFeet = currentUnit === 'ft';

    return (
      <>
        {isCentimeters && (
          <InputWrapper>
            <InputStyled
              type="number"
              placeholder="cm"
              name="cm"
              value={unitValues.cm?.toString() ?? initialValue?.toString() ?? ''}
              onChange={onChangeHandler}
            />
            <UnitLabel>{currentUnit}</UnitLabel>
          </InputWrapper>
        )}
        {isFeet && (
          <DoubleInputWrap>
            <InputWrapper>
              <InputStyled type="number" placeholder="ft" name="ft" value={unitValues.ft?.toString() ?? ''} onChange={onChangeHandler} />
              <UnitLabel>ft</UnitLabel>
            </InputWrapper>
            <InputWrapper>
              <InputStyled type="number" placeholder="in" name="in" value={unitValues.in?.toString() ?? ''} onChange={onChangeHandler} />
              <UnitLabel>in</UnitLabel>
            </InputWrapper>
          </DoubleInputWrap>
        )}
      </>
    );
  }, [currentUnit, unitValues, initialValue]);

  return (
    <Container>
      <UnitSwapToggle units={units} currentUnit={currentUnit} setCurrentUnit={handleUnitSwap} />
      {renderedInput}
    </Container>
  );
};

export default HeightInput;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const DoubleInputWrap = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const InputWrapper = styled.div`
  width: 71px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  gap: 4px;

  & > input {
    text-align: center;
  }
`;

const UnitLabel = styled.div`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: ${Colors.navyBlue};
`;
